import React, { useState } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import "../style/SignupPage.css";
import "../style/authFlow.css";

const passwordRules = [
  { label: "8+ characters", test: (value) => value.length >= 8 },
  { label: "Uppercase letter", test: (value) => /[A-Z]/.test(value) },
  { label: "Number", test: (value) => /\d/.test(value) },
  { label: "Special character", test: (value) => /[^A-Za-z0-9]/.test(value) },
];

const roleOptions = [
  {
    value: "student",
    label: "Student",
    description: "Best for learners who want practice, courses, and interview prep.",
  },
  {
    value: "trainer",
    label: "Trainer",
    description: "For mentors or team members. Admin may review trainer access.",
  },
];

export default function SignupPage() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("select");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [goal, setGoal] = useState("manual");
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const passwordScore = passwordRules.filter((rule) => rule.test(password)).length;

  const validateForm = () => {
    const nextErrors = {};

    if (!fullName.trim()) nextErrors.fullName = "Full name is required";
    if (!email.trim()) nextErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) nextErrors.email = "Enter a valid email";
    if (role === "select") nextErrors.role = "Please select a role";
    if (password.length < 8) nextErrors.password = "Use at least 8 characters";
    if (password !== confirmPassword) nextErrors.confirmPassword = "Passwords do not match";
    if (!acceptedPolicies) nextErrors.policies = "Please accept the Terms and Privacy Policy.";

    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return "Please fix the highlighted fields.";
    }

    return null;
  };

  const getFriendlySignupError = (error) => {
    switch (error.code) {
      case "auth/email-already-in-use":
        return "This email is already registered. Please sign in instead.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/weak-password":
        return "Password is too weak. Use at least 8 characters with a mix of letters and numbers.";
      case "auth/unauthorized-domain":
        return "This website domain is not authorized in Firebase Authentication.";
      default:
        return error.message || "Unable to create your account right now.";
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    const errorMsg = validateForm();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const trimmedFullName = fullName.trim();

      const res = await createUserWithEmailAndPassword(auth, normalizedEmail, password);

      await updateProfile(res.user, {
        displayName: trimmedFullName,
      });

      await setDoc(doc(db, "users", res.user.uid), {
        uid: res.user.uid,
        fullName: trimmedFullName,
        email: normalizedEmail,
        role,
        isActive: true,
        onboardingCompleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        profile: {
          avatar: "",
          phone: "",
        },
        metadata: {
          lastLogin: serverTimestamp(),
          signupSource: "web",
        },
        onboarding: {
          goal,
          level: "",
          interest: goal,
        },
      });

      toast.success("Account created successfully.");

      setTimeout(() => {
        navigate("/onboarding");
      }, 2000);
    } catch (error) {
      console.error(error);
      toast.error(getFriendlySignupError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-left">
        <div className="signup-box">
          <span className="auth-flow-eyebrow">Start free</span>
          <h1 className="welcome-text">Create Account</h1>
          <p className="subtitle">Join QA Coder and get a guided testing learning path.</p>

          <div className="auth-proof-grid">
            <span>500+ learners trained</span>
            <span>Project-based practice</span>
            <span>Resume and interview help</span>
          </div>

          <form onSubmit={handleSignup}>
            <div className="input-group">
              <label>Full Name</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                required
              />
              {formErrors.fullName && <small className="field-error">{formErrors.fullName}</small>}
            </div>

            <div className="input-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              {formErrors.email && <small className="field-error">{formErrors.email}</small>}
            </div>

            <div className="input-group">
              <label>Register as</label>
              <div className="role-card-grid">
                {roleOptions.map((option) => (
                  <button
                    type="button"
                    className={role === option.value ? "role-card selected" : "role-card"}
                    key={option.value}
                    onClick={() => setRole(option.value)}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </button>
                ))}
              </div>
              <p className="role-help">
                Choose student if you are learning. Trainer access may be reviewed by admin.
              </p>
              {formErrors.role && <small className="field-error">{formErrors.role}</small>}
            </div>

            <div className="input-group">
              <label>Your main goal</label>
              <select value={goal} onChange={(e) => setGoal(e.target.value)}>
                <option value="manual">Learn Manual Testing</option>
                <option value="automation">Learn Automation Testing</option>
                <option value="api">Learn API Testing</option>
                <option value="interview">Prepare for QA Interviews</option>
              </select>
            </div>

            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <div className="password-strength" aria-label="Password strength">
                <span className={passwordScore >= 1 ? "active" : ""} />
                <span className={passwordScore >= 2 ? "active" : ""} />
                <span className={passwordScore >= 3 ? "active" : ""} />
                <span className={passwordScore >= 4 ? "active" : ""} />
              </div>
              <small className="password-help">
                {passwordScore < 3 ? "Use 8+ characters with uppercase, number, and symbol." : "Good password strength."}
              </small>
              {formErrors.password && <small className="field-error">{formErrors.password}</small>}
            </div>

            <div className="input-group">
              <label>Confirm Password</label>
              <input
                type="password"
                placeholder="Repeat password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              {formErrors.confirmPassword && <small className="field-error">{formErrors.confirmPassword}</small>}
            </div>

            <label className="policy-consent-box">
              <input
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(e) => setAcceptedPolicies(e.target.checked)}
              />
              <span>
                I agree to QA Coder's{" "}
                <NavLink to="/terms" target="_blank" rel="noopener noreferrer">Terms</NavLink>
                ,{" "}
                <NavLink to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</NavLink>
                , and{" "}
                <NavLink to="/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</NavLink>
                .
              </span>
            </label>
            {formErrors.policies && <small className="field-error policy-error">{formErrors.policies}</small>}

            <button type="submit" className="btn-signup" disabled={loading}>
              {loading ? "Creating..." : "Create Account and Continue"}
            </button>
          </form>

          <p className="signin-text">
            Already have an account? <NavLink to="/login">Sign in</NavLink>
          </p>
        </div>
      </div>

      <div className="signup-right">
        <div className="auth-product-panel signup-panel">
          <span className="auth-panel-kicker">Personalized onboarding</span>
          <h2>Your account starts with a roadmap, not a blank dashboard.</h2>
          <div className="auth-panel-steps">
            <div>
              <strong>Goal</strong>
              <span>Pick Manual, Automation, API, or Interview Prep.</span>
            </div>
            <div>
              <strong>Level</strong>
              <span>Tell us if you are beginner, intermediate, or job-ready.</span>
            </div>
            <div>
              <strong>Next step</strong>
              <span>Continue to onboarding and get recommended modules.</span>
            </div>
          </div>
          <div className="auth-panel-metric">
            <strong>3 min</strong>
            <span>to create a learner profile and start your QA path</span>
          </div>
        </div>
      </div>
    </div>
  );
}
